// helpers / constants
import * as constants from 'utils/constants';
import { Text } from 'components';
import { FaFacebook } from 'react-icons/fa';
import { TwitterMonochrome } from 'icons';
import TagManager from 'react-gtm-module';
import { IconButton, HStack, Box, useColorModeValue, LightMode } from '@telescope/cassini-ui';
import { useWidget, useTheme } from '@telescope/cassini-hooks';
import { Open, ShareType } from 'types';
import { useSpinToWin } from 'features/spin-to-win-games';
import { useCallback } from 'react';

export const SocialSharing = () => {
  const { data } = useWidget({ select: (data: Open) => data.snapshot.snapshot_views.socialShares });
  const { content, settings, styles } = data!;

  const theme = useTheme();
  const { defaultColorScheme: c } = theme.settings;
  const hashtagColor = useColorModeValue(`${c}.600`, `${c}.200`);

  const { shouldEnterSweepsOnShare, enterSweepsOnShare } = useSpinToWin();

  const handleFacebookClick = useCallback(() => {
    TagManager.dataLayer({ dataLayer: { event: "socialShare", shareMethod: ShareType.FACEBOOK }});

    const { shareUrl = window.location.href } = content.facebook[shouldEnterSweepsOnShare? 'sweeps': 'default'];

    const url = new URL('https://www.facebook.com/sharer/sharer.php');
    url.searchParams.append('u', shareUrl);
    window.open(url, 'popup', 'width=600, height=400, scrollbars=no');

    shouldEnterSweepsOnShare && enterSweepsOnShare(ShareType.FACEBOOK);
  }, [shouldEnterSweepsOnShare])

  const handleTwitterClick = useCallback(() => {
    TagManager.dataLayer({ dataLayer: { event: "socialShare", shareMethod: ShareType.TWITTER }});

    const { copy, link } = content.twitter[shouldEnterSweepsOnShare? 'sweeps': 'default'];

    const url = new URL(constants.TWITTER_SHARE_URL);
    if (copy) url.searchParams.append('text', copy);
    if (link) url.searchParams.append('url', link);

    window.open(url, 'Twitter Share', 'width=550,height=420');

    shouldEnterSweepsOnShare && enterSweepsOnShare(ShareType.TWITTER);
  }, [shouldEnterSweepsOnShare])

  if(!settings.facebook_share && !settings.twitter_share) {
    return null;
  }

  return (
    <Box>
    { shouldEnterSweepsOnShare && 'sweepsCta' in content && 
      <Text fontWeight={800} mb={4} mt={-4} color="primary.500" fontSize="xl" textAlign="center">{content.sweepsCta}</Text> }

    <HStack spacing={3} justifyContent="center">
      { content.cta &&
        <Text fontSize="sm" fontWeight="500">{content.cta}</Text> }

      <LightMode>
        { settings.twitter_share &&
          <IconButton
            onClick={handleTwitterClick}
            minW="none"
            h="auto"
            fontSize={{ base: 'xl', md: '2xl' }}
            data-share-method="twitter"
            aria-label={content.twitter.iconAccessibility || 'Share on Twitter'}
            icon={<TwitterMonochrome />} 
            variant="link"
            colorScheme={styles.icons.colorScheme === 'brand' ? 'twitter' : styles.icons.colorScheme}
            /> }

        { settings.facebook_share &&
          <IconButton
            onClick={handleFacebookClick}
            minW="none"
            h="auto"
            fontSize={{ base: 'xl', md: '2xl' }}
            data-share-method="facebook"
            aria-label={content.facebook.iconAccessibility || 'Share on Facebook'}
            icon={<FaFacebook />} 
            variant="link"
            colorScheme={styles.icons.colorScheme === 'brand' ? 'facebook' : styles.icons.colorScheme}/> }
      </LightMode>

    </HStack>

    <Text fontWeight="700" my={4} textAlign="center" color={hashtagColor}>{content.hashtag}</Text>

    </Box>
  );
};